import React, { useContext, useEffect } from 'react';
import { RouterContext, Header, Footer, TripleAction, Content } from '../../../../gs-component-right-rail';
import get from 'lodash/get';
import { FastField as FormikField } from 'formik';
import { Field } from '../../../../gs-component-form';
import { FormRow, FormColumn } from '@xo-union/tk-component-form-grid';
import { Values, Question } from '../../../EventTypes';
import AnswerTypeButtons from './AnswerTypeButtons';
import OptionFields from './OptionFields';
import FormNote from './FormNote';
import styles from './styles.scss';

export interface QuestionFormProps {
  values: Values;
  isValid: boolean;
  isGeneralQuestion: boolean;
  question: Question | null;
  dirty?: boolean;
  submitForm: () => Promise<void>;
  resetForm: () => void;
  onClose: () => void;
  validateForm: () => void;
}

interface TripleActionProps {
  className?: string;
  primaryText: string;
  primaryOnClick: Function;
  primaryDisabled?: boolean;
  secondaryText?: string;
  secondaryOnClick?: Function;
  secondaryDisabled?: boolean;
  tertiaryText?: string;
  tertiaryOnClick?: Function;
  tertiaryDisabled?: boolean;
  showSpinner?: boolean;
  mode?: 'sideBySide' | 'stack' | 'stackFocus';
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  values,
  question,
  submitForm,
  onClose,
  resetForm,
  isGeneralQuestion,
  isValid,
  dirty,
  validateForm,
}) => {
  const { push } = useContext(RouterContext);
  const { id, options, type, answerType, text } = values;
  const formType = id ? 'edit' : 'add';

  const disableButtons = (): boolean => {
    if (answerType === 'Text') {
      return !(isValid && text);
    }

    return !(isValid && options && options.length > 1 && options.every(option => !!option.text.length));
  };

  const checkForEditedOptions = (): 'edit' | 'delete' | null => {
    const questionOptions = get(question, ['options']);
    const formOptions = get(values, ['options']) || [];
    if (!questionOptions) {
      return null;
    }

    let deletedOption = false;
    let editedOption = false;
    questionOptions.forEach(option => {
      const formOption = formOptions.find(f => f.id === option.id);

      if (!formOption) {
        deletedOption = true;
      } else if (formOption.text !== option.text) {
        editedOption = true;
      }
    });

    if (deletedOption) {
      return 'delete';
    }
    if (editedOption) {
      return 'edit';
    }
    return null;
  };

  useEffect(() => {
    id && validateForm();
  }, [id, validateForm]);

  const addQuestionHeader = (
    <Header onClose={(): void => (dirty ? push('discard') : onClose())}>
      <div className={styles.header}>
        <div className={styles.questionText}>Add Question</div>
      </div>
    </Header>
  );

  const editQuestionheader = (
    <Header onClose={(): void => (dirty ? push('discard') : onClose())}>
      <div className={styles.questionText}>Edit Question</div>
    </Header>
  );

  const tripleActionProps = (): TripleActionProps => {
    const baseProps = {
      primaryText: 'Save',
      primaryOnClick: async (): Promise<void> => {
        await submitForm();
        resetForm();
        onClose();
      },
      primaryDisabled: disableButtons(),
      secondaryText: 'Cancel',
      secondaryOnClick: async (): Promise<void> => {
        resetForm();
        onClose();
      },
      secondaryDisabled: false,
    };

    if (formType === 'edit') {
      return {
        ...baseProps,
        primaryText: 'Save',
        primaryOnClick: async (): Promise<void> => {
          if (formType === 'edit') {
            const action = checkForEditedOptions();
            if (action) {
              push(`confirm/${action}`);
              return;
            }
          }

          await submitForm();
          resetForm();
          onClose();
        },
        primaryDisabled: !dirty || disableButtons(),
        secondaryText: 'Cancel',
        secondaryOnClick: async (): Promise<void> => (dirty ? push('discard') : onClose()),
        secondaryDisabled: false,
        tertiaryText: 'Delete Question',
        tertiaryOnClick: async (): Promise<void> => {
          push('delete');
        },
        tertiaryDisabled: false,
      };
    }

    return baseProps;
  };

  // Determine which header to show based on the existence of an ID on the question.
  // If the question doesn't have an ID, it's a new question and we should show the add question header.
  // Otherwise, we are editing a question and should show that header.
  let header = addQuestionHeader;
  if (id && id !== '') {
    header = editQuestionheader;
  }

  return (
    <>
      {header}
      {formType === 'add' ? <FormNote /> : null}
      <Content>
        <div className={styles.content}>
          <FormRow>
            <FormColumn xs md>
              <FormikField name="text" label="Question Prompt*" id="text-field" data-testid="text" component={Field} />
            </FormColumn>
          </FormRow>
          <AnswerTypeButtons values={values} />
          {answerType === 'Option' && (
            <OptionFields
              displayInitialErrors={formType === 'edit'}
              values={values}
              type={type}
              isGeneralQuestion={isGeneralQuestion}
            />
          )}
        </div>
      </Content>
      <Footer>
        <TripleAction {...tripleActionProps()} className={styles.tripleAction} />
      </Footer>
    </>
  );
};

export default QuestionForm;
